<template>
	<el-form ref="emailForm" :model="localValue" :rules="emailFormRules" label-width="140px" autocomplete="off">
		<el-form-item :label="$t('from')" prop="from">
			<el-select
				v-model="localValue.from"
				:disabled="!editable"
				filterable
				style="width: 420px"
				size="small"
				@change="handleInput"
			>
				<el-option
					v-for="(address, index) in emailAddressesFrom"
					:key="index"
					:label="formatFromAddress(address)"
					:value="address.email_from"
				>
					<div style="display: flex; flex-direction: column;">
						<div style="font-size: 13px;">
							<span v-if="address.email_from_name" style="font-weight: 600;">{{ address.email_from_name }}</span>
							<span style="color: #8492a6; margin-left: 4px;">{{ address.email_from }}</span>
						</div>
					</div>
				</el-option>
			</el-select>
		</el-form-item>

		<el-form-item :label="$t('to')" prop="to">
			<el-input v-model="localValue.to" class="hidden-input" tabindex="-1" autocomplete="new-password" />

			<div class="tag-input-container" :class="{ focused: toInputFocused, disabled: !editable }" @click="focusToInput">
				<div class="tags-and-input">
					<el-tag
						v-for="(tag, index) in toTags"
						:key="'to-' + index"
						size="small"
						closable
						:disable-transitions="false"
						class="input-tag"
						@close.stop="removeToTag(tag)"
					>
						{{ tag }}
					</el-tag>
					<input
						ref="toInput"
						v-model="toInputValue"
						:disabled="!editable"
						class="tag-input"
						style="width:420px"
						type="text"
						autocomplete="new-password"
						@focus="handleFocus('to')"
						@blur="handleBlur('to')"
						@keydown="handleTagInputKeydown($event, 'to')"
						@input="handleInput()"
					/>
				</div>
				<div v-if="endpointSuggestions.length > 0 && toInputFocused" class="suggestions-dropdown">
					<div
						v-for="(suggestion, index) in endpointSuggestions"
						:key="'to-suggestion-' + index"
						class="suggestion-item"
						@mousedown.prevent="selectSuggestion('to', suggestion)"
					>
						{{ suggestion }}
					</div>
				</div>
			</div>
			<template v-if="suggestedTo && suggestedTo.length > 0">
				<p class="input-help">{{ $t('suggested_to_lower') }}: {{ suggestedTo.join(', ') }}</p>
			</template>
			<p class="input-help">{{ $t('action_detail.input-help.to') }}</p>
		</el-form-item>

		<el-form-item :label="$t('Cc')" prop="cc">
			<el-input v-model="localValue.cc" class="hidden-input" tabindex="-1" autocomplete="new-password" />
			<div class="tag-input-container" :class="{ focused: ccInputFocused, disabled: !editable }" @click="focusCcInput">
				<div class="tags-and-input">
					<el-tag
						v-for="(tag, index) in ccTags"
						:key="'cc-' + index"
						size="small"
						closable
						:disable-transitions="false"
						class="input-tag"
						@close.stop="removeCcTag(tag)"
					>
						{{ tag }}
					</el-tag>
					<input
						ref="ccInput"
						v-model="ccInputValue"
						:disabled="!editable"
						class="tag-input"
						type="text"
						autocomplete="new-password"
						style="width:580px"
						@focus="handleFocus('cc')"
						@blur="handleBlur('cc')"
						@keydown="handleTagInputKeydown($event, 'cc')"
						@input="handleInput()"
					/>
				</div>
				<div v-if="endpointSuggestions.length > 0 && ccInputFocused" class="suggestions-dropdown">
					<div
						v-for="(suggestion, index) in endpointSuggestions"
						:key="'cc-suggestion-' + index"
						class="suggestion-item"
						@mousedown.prevent="selectSuggestion('cc', suggestion)"
					>
						{{ suggestion }}
					</div>
				</div>
			</div>
			<p class="input-help">{{ $t('action_detail.input-help.cc') }}</p>
		</el-form-item>

		<el-form-item :label="$t('bcc')" prop="bcc">
			<el-input v-model="localValue.bcc" class="hidden-input" tabindex="-1" autocomplete="new-password" />

			<div class="tag-input-container" :class="{ focused: bccInputFocused, disabled: !editable }" @click="focusBccInput">
				<div class="tags-and-input">
					<el-tag
						v-for="(tag, index) in bccTags"
						:key="'bcc-' + index"
						size="small"
						closable
						:disable-transitions="false"
						class="input-tag"
						@close.stop="removeBccTag(tag)"
					>
						{{ tag }}
					</el-tag>
					<input
						ref="bccInput"
						v-model="bccInputValue"
						:disabled="!editable"
						class="tag-input"
						style="width:580px"
						type="text"
						autocomplete="new-password"
						@focus="handleFocus('bcc')"
						@blur="handleBlur('bcc')"
						@keydown="handleTagInputKeydown($event, 'bcc')"
						@input="handleInput()"
					/>
				</div>
				<div v-if="endpointSuggestions.length > 0 && bccInputFocused" class="suggestions-dropdown">
					<div
						v-for="(suggestion, index) in endpointSuggestions"
						:key="'bcc-suggestion-' + index"
						class="suggestion-item"
						@mousedown.prevent="selectSuggestion('bcc', suggestion)"
					>
						{{ suggestion }}
					</div>
				</div>
			</div>
			<p class="input-help">{{ $t('action_detail.input-help.cc') }}</p>
		</el-form-item>
		<el-form-item :label="$t('subject')" prop="subject">
			<el-input v-model="localValue.subject" class="hidden-input" tabindex="-1" autocomplete="new-password" />

			<div class="subject-input-container" :class="{ focused: subjectInputFocused, disabled: !editable }">
				<input
					ref="subjectInput"
					v-model="localValue.subject"
					:disabled="!editable"
					class="subject-input"
					type="text"
					autocomplete="new-password"
					@focus="subjectInputFocused = true"
					@blur="subjectInputFocused = false"
					@input="handleInput()"
				/>
			</div>
		</el-form-item>
	</el-form>
</template>

<script>
import Utils from '@/utils/utils';
import gql from 'graphql-tag';
import { debounce } from 'lodash';
import { mapGetters } from 'vuex';

export default {
	name: 'EmailForm',
	props: {
		value: {
			type: Object,
			default: () => ({
				from: '',
				to: '',
				cc: '',
				bcc: '',
				subject: ''
			})
		},
		editable: {
			type: Boolean,
			default: true
		},
		suggestedTo: {
			type: Array,
			default: () => []
		},
		emailAddressesFrom: {
			type: Array,
			default: () => []
		}
	},
	apollo: {
		endpointSuggestions: {
			query: gql`
				query componentsMessagingEmailForm__endpointSuggestions(
					$participant: uuid!
					$legalEntity: uuid
					$searchTerm: String!
				) {
					risolto_reference_data(
						where: {
							reference_data_type: { _eq: "EMAIL_AUTOCOMPLETE" }
							participant: { _eq: $participant }
							_or: [
								{ legal_entity: { _eq: $legalEntity } }
								{ legal_entity: { _is_null: true } }
							]
							key: { _ilike: $searchTerm }
						}
					) {
						key
					}
				}
			`,
			variables() {
				return {
					participant: this.selectedParticipantStore,
					legalEntity: this.selectedLegalEntityStore || null,
					searchTerm: this.searchTerm ? `%${this.searchTerm}%` : '%%'
				};
			},
			skip() {
				return !this.searchTerm || this.searchTerm.length < 3;
			},
			update(data) {
				return data.risolto_reference_data.map(item => item.key);
			},
			error(error) {
				console.error('Error fetching endpoint suggestions:', error);
			}
		}
	},
	data() {
		const validateSingleEmail = (rule, value, callback) => {
			// @ts-ignore
			if (!this.validateEmail(value)) {
				// @ts-ignore
				callback(new Error(this.$t('validator.action.email.is_invalid')));
				return;
			}

			callback();
		};
		const validateMultipleEmail = (rule, value, callback) => {
			let emailAddresses = value
				.split(';')
				.flatMap(x => x.split(','))
				.map(x => x.trim())
				.filter(x => x !== '');
			// @ts-ignore
			let validEmailAddresses = emailAddresses.filter(x => this.validateEmail(x));

			if (emailAddresses.length !== 0 && validEmailAddresses.length !== emailAddresses.length) {
				// @ts-ignore
				callback(new Error(this.$t('validator.action.email.is_invalid')));
				return;
			}

			callback();
		};

		return {
			searchTerm: '',
			endpointSuggestions: [],
			localValue: {
				from: '',
				to: '',
				cc: '',
				bcc: '',
				subject: ''
			},
			toInputValue: '',
			ccInputValue: '',
			bccInputValue: '',
			toTags: [],
			ccTags: [],
			bccTags: [],
			toInputFocused: false,
			ccInputFocused: false,
			bccInputFocused: false,
			subjectInputFocused: false,
			initialized: false,
			emailFormRules: {
				from: [{ required: true, message: this.$t('validator.action.email.from.is_required'), trigger: 'change' }, { validator: validateSingleEmail, trigger: 'change'}],
				to: [{ required: true, message: this.$t('validator.action.email.to.is_required'), trigger: 'change' }, { validator: validateSingleEmail, trigger: 'change'}],
				cc: [{ validator: validateMultipleEmail, trigger: 'change'}],
				bcc: [{ validator: validateMultipleEmail, trigger: 'change'}],
				subject: [{ required: true, message: this.$t('validator.action.email.subject.is_required'), trigger: 'change' }]
			}
		};
	},
	computed: {
		...mapGetters(['selectedParticipantStore', 'selectedLegalEntityStore'])
	},
	watch: {
		value: {
			handler(newVal) {
				// Update localValue from prop
				this.localValue = { ...this.localValue, ...newVal };
				this.updateTagsFromValue(newVal);
			},
			immediate: true,
			deep: true
		},
		toInputValue(val) {
			this.debouncedSearch(val);
		},
		ccInputValue(val) {
			this.debouncedSearch(val);
		},
		bccInputValue(val) {
			this.debouncedSearch(val);
		}
	},
	methods: {
		validate(callback) {
			this.$refs.emailForm.validate(callback);
		},
		debouncedSearch: debounce(function(value) {
			if (value && value.length >= 3) {
				this.searchTerm = value;
			} else {
				this.searchTerm = '';
				this.endpointSuggestions = [];
			}
		}, 300),
		updateTagsFromValue(val) {
			// Convert incoming value to tags
			// Skip if we're currently editing (input is focused)
			if (!this.toInputFocused && val.to !== undefined) {
				const newToTags = val.to ? val.to.split(/[,;]/).map(email => email.trim()).filter(email => email && Utils.isValidEmail(email)) : [];
				// Only update if tags have actually changed to avoid unnecessary re-renders
				if (JSON.stringify(newToTags) !== JSON.stringify(this.toTags)) {
					this.toTags = newToTags;
				}
			}

			if (!this.ccInputFocused && val.cc !== undefined) {
				const newCcTags = val.cc ? val.cc.split(/[,;]/).map(email => email.trim()).filter(email => email && Utils.isValidEmail(email)) : [];
				if (JSON.stringify(newCcTags) !== JSON.stringify(this.ccTags)) {
					this.ccTags = newCcTags;
				}
			}

			if (!this.bccInputFocused && val.bcc !== undefined) {
				const newBccTags = val.bcc ? val.bcc.split(/[,;]/).map(email => email.trim()).filter(email => email && Utils.isValidEmail(email)) : [];
				if (JSON.stringify(newBccTags) !== JSON.stringify(this.bccTags)) {
					this.bccTags = newBccTags;
				}
			}

			this.initialized = true;
		},

		getTagsForField(field) {
			if (field === 'to') return this.toTags;
			if (field === 'cc') return this.ccTags;
			if (field === 'bcc') return this.bccTags;
			return [];
		},

		formatFromAddress(address) {
			if (address.email_from_name) {
				return `${address.email_from_name} <${address.email_from}>`;
			}
			return address.email_from;
		},

		focusToInput() {
			if (this.editable) {
				this.$refs.toInput.focus();
			}
		},

		focusCcInput() {
			if (this.editable) {
				this.$refs.ccInput.focus();
			}
		},

		focusBccInput() {
			if (this.editable) {
				this.$refs.bccInput.focus();
			}
		},

		focusSubjectInput() {
			if (this.editable) {
				this.$refs.subjectInput.focus();
			}
		},

		handleBlur(field) {
			if (field === 'to') this.toInputFocused = false;
			if (field === 'cc') this.ccInputFocused = false;
			if (field === 'bcc') this.bccInputFocused = false;

			// If there's a valid email in the input, add it as a tag
			const value = this.getInputValueForField(field).trim();
			if (value && this.validateEmail(value)) {
				this.addTag(field, value);
			}
		},

		getInputValueForField(field) {
			if (field === 'to') return this.toInputValue;
			if (field === 'cc') return this.ccInputValue;
			if (field === 'bcc') return this.bccInputValue;
			return '';
		},

		handleTagInputKeydown(event, field) {
			// Handle special keys for tag creation
			if (['Enter', 'Tab', ' ', ',', ';'].includes(event.key) && event.target.value.trim()) {
				event.preventDefault();

				const value = event.target.value.trim();
				if (this.validateEmail(value)) {
					this.addTag(field, value);
				}
			}

			// Remove the last tag when pressing backspace in an empty input field
			if (event.key === 'Backspace' && !event.target.value) {
				const tags = this.getTagsForField(field);
				if (tags.length > 0) {
					const lastTag = tags[tags.length - 1];
					if (field === 'to') {
						this.removeToTag(lastTag);
					} else if (field === 'cc') {
						this.removeCcTag(lastTag);
					} else if (field === 'bcc') {
						this.removeBccTag(lastTag);
					}
				}
			}
		},

		handleInput() {
			// Only update the form value, don't create tags
			// Tags are only created on specific key presses or when selecting a suggestion
			const updatedValue = {
				from: this.localValue.from,
				to: this.combineTagsAndInput('to'),
				cc: this.combineTagsAndInput('cc'),
				bcc: this.combineTagsAndInput('bcc'),
				subject: this.localValue.subject
			};
			this.$emit('input', updatedValue);
			this.$emit('change');
		},

		addTag(field, value) {
			if (!value || !this.validateEmail(value)) return;

			if (field === 'to') {
				// For 'to' field, limit to only one email
				if (!this.toTags.includes(value)) {
					this.toTags = [value]; // Replace any existing tags with just this one
					this.toInputValue = '';
					// Update localValue to reflect the new tag
					this.localValue.to = this.combineTagsAndInput('to');
				}
			} else if (field === 'cc') {
				if (!this.ccTags.includes(value)) {
					this.ccTags.push(value);
					this.ccInputValue = '';
					// Update localValue to reflect the new tag
					this.localValue.cc = this.combineTagsAndInput('cc');
				}
			} else if (field === 'bcc') {
				if (!this.bccTags.includes(value)) {
					this.bccTags.push(value);
					this.bccInputValue = '';
					// Update localValue to reflect the new tag
					this.localValue.bcc = this.combineTagsAndInput('bcc');
				}
			}
			// Emit the updated value
			this.$emit('input', this.localValue);
			this.$emit('change');
		},

		removeToTag(tag) {
			this.toTags = this.toTags.filter(t => t !== tag);
			// Update localValue to reflect the removed tag
			this.localValue.to = this.combineTagsAndInput('to');
			// Emit the updated value
			this.$emit('input', this.localValue);
			this.$emit('change');
		},

		removeCcTag(tag) {
			this.ccTags = this.ccTags.filter(t => t !== tag);
			// Update localValue to reflect the removed tag
			this.localValue.cc = this.combineTagsAndInput('cc');
			// Emit the updated value
			this.$emit('input', this.localValue);
			this.$emit('change');
		},

		removeBccTag(tag) {
			this.bccTags = this.bccTags.filter(t => t !== tag);
			// Update localValue to reflect the removed tag
			this.localValue.bcc = this.combineTagsAndInput('bcc');
			// Emit the updated value
			this.$emit('input', this.localValue);
			this.$emit('change');
		},

		selectSuggestion(field, suggestion) {
			this.addTag(field, suggestion);

			// Focus the input after selecting a suggestion
			if (field === 'to') this.$refs.toInput.focus();
			if (field === 'cc') this.$refs.ccInput.focus();
			if (field === 'bcc') this.$refs.bccInput.focus();
		},

		validateEmail(email) {
			return Utils.isValidEmail(email);
		},

		combineTagsAndInput(field) {
			// Combine tags with the current input value without creating new tags
			const tags = this.getTagsForField(field);
			const inputValue = this.getInputValueForField(field).trim();

			if (tags.length === 0) {
				return inputValue;
			}

			if (inputValue) {
				return tags.join(', ') + ', ' + inputValue;
			}

			return tags.join(', ');
		},

		handleFocus(field) {
			if (field === 'to') this.toInputFocused = true;
			if (field === 'cc') this.ccInputFocused = true;
			if (field === 'bcc') this.bccInputFocused = true;

			const value = this.getInputValueForField(field);

			if (value && value.length >= 3) {
				this.searchTerm = value;
			} else {
				if (field === 'to') {
					this.endpointSuggestions = this.suggestedTo;
				}
			}
		}
	}
};
</script>

<style lang="scss" scoped>
@import "@/styles/variables.scss";

.hidden-input {
	display: none;
}

.input-help {
	font-size: 12px;
	line-height: 1.3;
	margin: 0;
	color: $grey;
}

.subject-input-container {
	display: inline-flex;
	position: relative;
	min-height: 32px;
	width: 580px;
	border: 1px solid #dcdfe6;
	border-radius: 4px;
	background-color: #fff;
	transition: border-color 0.2s;

	&.focused {
		border-color: #ed6b1c;
	}

	&.disabled {
		background-color: #f5f7fa;
		border-color: #e4e7ed;
		color: #c0c4cc;
		cursor: not-allowed;
	}
}

.subject-input {
	width: 100%;
	height: 32px;
	border: none;
	outline: none;
	background: transparent;
	padding: 0 10px;

	&:disabled {
		background-color: transparent;
		cursor: not-allowed;
	}
}

.tag-input-container {
	display: inline-flex;
	flex-direction: column;
	position: relative;
	min-height: 32px;
	border: 1px solid #dcdfe6;
	border-radius: 4px;
	background-color: #fff;
	transition: border-color 0.2s;

	&.focused {
		border-color: #ed6b1c;
	}

	&.disabled {
		background-color: #f5f7fa;
		border-color: #e4e7ed;
		color: #c0c4cc;
		cursor: not-allowed;
	}

	.tags-and-input {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		padding: 2px 5px;
		min-height: 28px;
		width: 100%;
	}

	.input-tag {
		margin: 2px 5px 2px 0;
		max-width: 100%;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	.tag-input {
		flex: 0 0 auto;
		border: none;
		outline: none;
		background: transparent;
		min-width: 80px;
		max-width: 420px;
		width: auto;
		height: 24px;
		padding: 0 5px;

		&:disabled {
			background-color: transparent;
			cursor: not-allowed;
		}
	}

	.suggestions-dropdown {
		position: absolute;
		top: 100%;
		left: 0;
		width: 100%;
		max-height: 200px;
		overflow-y: auto;
		background-color: #fff;
		border: 1px solid #dcdfe6;
		border-radius: 4px;
		box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
		z-index: 100;
		margin-top: 5px;
	}

	.suggestion-item {
		padding: 8px 10px;
		cursor: pointer;

		&:hover {
			background-color: #f5f7fa;
		}
	}
}
</style>
